// src/index.js
// Import process polyfill first to ensure it's available to all modules
import './utils/processPolyfill';
import { SHAMPOO } from './data/canadianShampoo';
import { CONDITIONER } from './data/canadianConditioner';

// Import all other modules after
import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import { UserProvider } from './context/UserContext';
import { CollectionsProvider } from './context/CollectionsContext';
import { UserValuesProvider } from './context/UserValuesContext';
import { ErrorBoundary } from './ErrorBoundary';
import HydrationErrorBoundary from './components/HydrationErrorBoundary';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import { initClientOptimizations } from './utils/criticalCss';
import { getEnv } from './utils/env';
import { initPerformanceMonitoring } from './utils/performance';
import { createRoot, hydrateRoot } from 'react-dom/client';

// Initialize performance monitoring only in browser
if (typeof window !== 'undefined') {
  initPerformanceMonitoring();
}

// Add browser features detection as data attributes on the HTML element
if (typeof window !== 'undefined' && typeof navigator !== 'undefined' && typeof document !== 'undefined') {
  if ('connection' in navigator) {
    document.documentElement.dataset.connectionType = navigator.connection.effectiveType;
    document.documentElement.dataset.saveData = navigator.connection.saveData;
  }

  document.documentElement.dataset.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// Initialize client optimizations as early as possible
if (typeof window !== 'undefined') {
  // Define deferred styles to load after initial render
  window.DEFERRED_STYLES = []; // Set to empty array
  
  // Start client-side optimization before React hydration
  initClientOptimizations(); // Uncommented
}

// Check if the page was prerendered
const rootElement = typeof document !== 'undefined' ? document.getElementById('root') : null;
const isProduction = process.env.NODE_ENV === 'production';
const isPrerendered =
  isProduction &&
  typeof document !== 'undefined' &&
  window.location.pathname.replace(/\/$/, '') !== SHAMPOO.path &&
  window.location.pathname.replace(/\/$/, '') !== CONDITIONER.path &&
  !!rootElement?.hasAttribute('data-prerendered') &&
  !!rootElement?.hasChildNodes();

// Create the application jsx
const application = (
  <HydrationErrorBoundary>
    <ErrorBoundary>
      <BrowserRouter>
        <UserProvider>
          <CollectionsProvider>
            <UserValuesProvider>
              <App />
            </UserValuesProvider>
          </CollectionsProvider>
        </UserProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </HydrationErrorBoundary>
);

// Hydrate or render the app
if (isPrerendered) {
  hydrateRoot(rootElement, application);
} else {
  createRoot(rootElement).render(application);
}

// Add a data attribute to the HTML tag once the app has been hydrated
if (typeof document !== 'undefined') {
  document.documentElement.dataset.hydrated = 'true';
}

// Service worker is intentionally NOT registered.
//
// The previous Workbox precache service worker served a cached /index.html app
// shell for every navigation, which caused stale JS bundles (that survived
// CloudFront invalidations and hard reloads) and hydration mismatches
// (React #418/#423) on prerendered routes. We now ship a self-destroying
// public/service-worker.js kill switch that unregisters itself and clears
// caches for existing users. Calling unregister() here removes any service
// worker still registered on a client. Do NOT call register() again or the
// kill switch would re-install in a loop.
serviceWorkerRegistration.unregister();

// Measure and report performance metrics
reportWebVitals(metric => {
  // Send to analytics service in production
  if (getEnv('NODE_ENV') === 'production') {
    // Example: Send to Google Analytics
    // window.gtag('event', name, { value: delta, metric_id: id });
    
    // Log to console in development
    console.log(metric);
  }
});
